/**
 * Generic typed CRUD client for a single DRF resource.
 *
 * Every screen used to hand-roll its own `warehouses/api/suppliers/${id}/`
 * string interpolation, its own list/create/update/delete calls, and its own
 * response unwrapping. `createResourceApi` centralizes that into five typed
 * methods per resource, built on top of the existing `api` fetch wrapper.
 *
 * Usage (see src/entities/*.ts for full examples):
 *   export const supplierApi = createResourceApi<Supplier>('warehouses/api/suppliers');
 */

import { api } from './api';
import type { PaginatedResponse } from './types';

export type ResourceId = number | string;

export interface ListQuery {
  [key: string]: string | number | boolean | undefined | null;
}

export interface ResourceApi<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  /** GET <base>/ — unwrapped array (DRF pagination stripped, like plain api.get). */
  list(query?: ListQuery): Promise<T[]>;
  /** GET <base>/ — raw paginated envelope ({ count, next, previous, results }). */
  listPage(query?: ListQuery): Promise<PaginatedResponse<T>>;
  /** GET <base>/<id>/ */
  get(id: ResourceId): Promise<T>;
  /** POST <base>/ */
  create(data: TCreate): Promise<T>;
  /** PATCH <base>/<id>/ — partial update. */
  update(id: ResourceId, data: TUpdate): Promise<T>;
  /** PUT <base>/<id>/ — full replace. */
  replace(id: ResourceId, data: TCreate): Promise<T>;
  /** DELETE <base>/<id>/ */
  remove(id: ResourceId): Promise<void>;
}

function toQueryString(query?: ListQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function createResourceApi<T, TCreate = Partial<T>, TUpdate = Partial<T>>(
  basePath: string
): ResourceApi<T, TCreate, TUpdate> {
  const base = basePath.replace(/\/+$/, '');
  return {
    list: (query) => api.get<T[]>(`${base}/${toQueryString(query)}`),
    listPage: (query) => api.getRaw(`${base}/${toQueryString(query)}`),
    get: (id) => api.get<T>(`${base}/${id}/`),
    create: (data) => api.post<T>(`${base}/`, data),
    update: (id, data) => api.patch<T>(`${base}/${id}/`, data),
    replace: (id, data) => api.put<T>(`${base}/${id}/`, data),
    remove: (id) => api.delete(`${base}/${id}/`) as Promise<void>,
  };
}
